import { useState, useMemo } from 'react'
import { startOfISOWeek, endOfISOWeek, addWeeks, subWeeks, format, eachDayOfInterval, parseISO } from 'date-fns'
import { useMealPlan } from './hooks/useMealPlan'
import { useRecipes } from '../recipes/hooks/useRecipes'
import { useShoppingLists } from '../shopping/hooks/useShoppingLists'
import { MealPicker } from './components/MealPicker'
import { Spinner } from '../../components/ui/Spinner'
import { Button } from '../../components/ui/Button'
import { supabase } from '../../lib/supabase'
import { cn } from '../../lib/utils'
import { Portal } from '../../components/Portal'

const SLOTS = ['breakfast', 'lunch', 'dinner', 'snack']
const SLOT_LABEL = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack' }

const AddToShoppingModal = ({ weekRecipes, lists, onClose }) => {
  const [mode,    setMode]    = useState(lists?.length ? 'existing' : 'new')
  const [listId,  setListId]  = useState(lists?.[0]?.id ?? '')
  const [newName, setNewName] = useState('')
  const { createList } = useShoppingLists()
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState('')

  const allIngredients = useMemo(() => {
    const items = []
    for (const r of weekRecipes) {
      for (const ing of r.ingredients ?? []) {
        if (ing.name) items.push({
          name:     ing.name,
          quantity: [ing.amount, ing.unit].filter(Boolean).join(' ') || null,
          category: r.title,
        })
      }
    }
    return items
  }, [weekRecipes])

  const handleAdd = async () => {
    setSaving(true)
    setError('')
    try {
      let targetId = listId
      if (mode === 'new') {
        const list = await createList(newName.trim() || 'Meal Plan Ingredients')
        targetId = list.id
      }
      const { data: { session } } = await supabase.auth.getSession()
      const rows = allIngredients.map((i) => ({ ...i, list_id: targetId, added_by: session.user.id }))
      const { error: err } = await supabase.from('shopping_items').insert(rows)
      if (err) throw err
      onClose()
    } catch (err) {
      setError(err.message)
    } finally { setSaving(false) }
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 pb-[max(16px,env(safe-area-inset-bottom,16px))] sm:pb-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-surface rounded-2xl w-full max-w-sm p-6 space-y-4 shadow-card max-h-[90dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-bold text-text-primary">Add to Shopping List</h3>
        <p className="text-sm text-text-muted">
          {allIngredients.length} ingredient{allIngredients.length !== 1 ? 's' : ''} from {weekRecipes.length} recipe{weekRecipes.length !== 1 ? 's' : ''}
        </p>

        {lists?.length > 0 && (
          <div className="flex gap-2">
            {['existing', 'new'].map((m) => (
              <button key={m} onClick={() => setMode(m)}
                className={cn('flex-1 py-1.5 text-sm rounded-lg border transition-colors',
                  mode === m ? 'border-primary text-primary bg-primary-light' : 'border-border text-text-muted')}>
                {m === 'existing' ? 'Existing List' : 'New List'}
              </button>
            ))}
          </div>
        )}

        {mode === 'existing' && lists?.length > 0 ? (
          <select value={listId} onChange={(e) => setListId(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
            {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        ) : (
          <input type="text" placeholder="List name (optional)" value={newName} onChange={(e) => setNewName(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
        )}

        {error && <p className="text-sm text-rose-600">{error}</p>}

        <div className="flex gap-2">
          <Button variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="flex-1" onClick={handleAdd} disabled={saving || allIngredients.length === 0}>
            {saving ? <Spinner size="sm" /> : 'Add Ingredients'}
          </Button>
        </div>
      </div>
    </div>
    </Portal>
  )
}

export const MealsPage = () => {
  const [cursor, setCursor] = useState(new Date())
  const [picker, setPicker] = useState(null)
  const [showAddToShopping, setShowAddToShopping] = useState(false)

  const weekStart = format(startOfISOWeek(cursor), 'yyyy-MM-dd')
  const weekEnd   = format(endOfISOWeek(cursor), 'yyyy-MM-dd')

  const { data: slots = [], isLoading, upsertSlot, clearSlot } = useMealPlan(weekStart, weekEnd)
  const { data: recipes = [] }       = useRecipes()
  const { data: shoppingLists = [] } = useShoppingLists()

  const days = eachDayOfInterval({ start: parseISO(weekStart), end: parseISO(weekEnd) })

  const slotMap = useMemo(() => {
    const map = {}
    for (const s of slots) map[`${s.date}_${s.slot_name}`] = s
    return map
  }, [slots])

  const getSlot = (day, slotName) => slotMap[`${format(day, 'yyyy-MM-dd')}_${slotName}`]

  const weekRecipeIds = useMemo(() => [...new Set(slots.filter((s) => s.recipe_id).map((s) => s.recipe_id))], [slots])
  const weekRecipes   = useMemo(() => recipes.filter((r) => weekRecipeIds.includes(r.id)), [recipes, weekRecipeIds])

  const handleCellTap = (day, slotName) => {
    setPicker({ date: format(day, 'yyyy-MM-dd'), slotName, currentSlot: getSlot(day, slotName) })
  }

  return (
    <div className="flex flex-col h-full overflow-hidden max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(subWeeks(cursor, 1))} className="p-1.5 rounded-lg hover:bg-background">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6" /></svg>
          </button>
          <span className="text-sm font-bold text-text-primary text-center">
            {format(parseISO(weekStart), 'MMM d')} – {format(parseISO(weekEnd), 'MMM d')}
          </span>
          <button onClick={() => setCursor(addWeeks(cursor, 1))} className="p-1.5 rounded-lg hover:bg-background">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
          </button>
        </div>
        {weekRecipes.length > 0 && (
          <button onClick={() => setShowAddToShopping(true)}
            className="text-xs text-primary font-semibold flex items-center gap-1 hover:opacity-80">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
            Add to List
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center"><Spinner /></div>
      ) : (
        <div className="flex-1 overflow-auto">
          <div className="min-w-[600px]">
            {/* Day headers */}
            <div className="grid grid-cols-8 border-b border-border sticky top-0 bg-surface z-10">
              <div className="p-2 text-xs font-semibold text-text-muted border-r border-border" />
              {days.map((day) => (
                <div key={format(day, 'yyyy-MM-dd')} className="p-2 text-center border-l border-border">
                  <div className="text-[10px] text-text-muted">{format(day, 'EEE')}</div>
                  <div className="text-sm font-bold text-text-primary">{format(day, 'd')}</div>
                </div>
              ))}
            </div>

            {SLOTS.map((slotName) => (
              <div key={slotName} className="grid grid-cols-8 border-b border-border">
                <div className="p-2 text-xs font-semibold text-text-muted flex items-center bg-surface border-r border-border">
                  {SLOT_LABEL[slotName]}
                </div>
                {days.map((day) => {
                  const slot = getSlot(day, slotName)
                  return (
                    <button
                      key={format(day, 'yyyy-MM-dd')}
                      onClick={() => handleCellTap(day, slotName)}
                      className="border-l border-border min-h-[72px] p-1.5 text-left hover:bg-background transition-colors text-xs relative"
                    >
                      {slot ? (
                        <div className="h-full bg-primary-light rounded-lg p-1.5">
                          <div className="font-semibold text-primary leading-tight text-[11px] line-clamp-2">
                            {slot.recipes?.title ?? slot.custom_name}
                          </div>
                          {slot.recipes?.cook_time_minutes && (
                            <div className="text-[10px] text-text-muted mt-0.5">{slot.recipes.cook_time_minutes}m</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-text-muted/40 text-xl leading-none">+</span>
                      )}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {picker && (
        <MealPicker
          date={picker.date}
          slotName={picker.slotName}
          currentSlot={picker.currentSlot}
          recipes={recipes}
          onSave={async (args) => { await upsertSlot(args); setPicker(null) }}
          onClear={async (args) => { await clearSlot(args); setPicker(null) }}
          onClose={() => setPicker(null)}
        />
      )}

      {showAddToShopping && (
        <AddToShoppingModal
          weekRecipes={weekRecipes}
          lists={shoppingLists}
          onClose={() => setShowAddToShopping(false)}
        />
      )}
    </div>
  )
}
